import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HandThumbUpIcon, 
  PencilIcon,
  CheckIcon,
  PhotoIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { HandThumbUpIcon as ThumbUpSolid } from '@heroicons/react/24/solid';
import WriteReviewModal from './WriteReviewModal';
import { dtLocale } from '../../utils/dateLocale';

// ✅ Image Lightbox Modal Component
const ImageLightbox = memo(({ photos, initialIndex, isOpen, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handlePrevious = useCallback((e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  }, [photos.length]);

  const handleNext = useCallback((e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === photos.length - 1 ? 0 : prev + 1));
  }, [photos.length]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowLeft') handlePrevious(e);
    if (e.key === 'ArrowRight') handleNext(e);
    if (e.key === 'Escape') onClose();
  }, [handlePrevious, handleNext, onClose]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10001,
              transition: 'all 0.2s'
            }}
          >
            <XMarkIcon style={{ width: '24px', height: '24px', color: 'white' }} />
          </button>

          <div style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600',
            background: 'rgba(0, 0, 0, 0.5)',
            padding: '8px 16px',
            borderRadius: '20px',
            zIndex: 10001
          }}>
            {currentIndex + 1} / {photos.length}
          </div>

          {photos.length > 1 && (
            <button
              onClick={handlePrevious}
              style={{
                position: 'absolute',
                left: '20px',
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001,
                transition: 'all 0.2s'
              }}
            >
              <ChevronLeftIcon style={{ width: '24px', height: '24px', color: 'white' }} />
            </button>
          )}

          <motion.img
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            src={photos[currentIndex]}
            alt={`Review photo ${currentIndex + 1}`}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: '12px',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
            }}
          />

          {photos.length > 1 && (
            <button
              onClick={handleNext}
              style={{
                position: 'absolute',
                right: '20px',
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001,
                transition: 'all 0.2s'
              }}
            >
              <ChevronRightIcon style={{ width: '24px', height: '24px', color: 'white' }} />
            </button>
          )}

          {photos.length > 1 && (
            <div style={{
              position: 'fixed',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '8px',
              padding: '12px',
              background: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '12px',
              backdropFilter: 'blur(10px)',
              overflowX: 'auto',
              maxWidth: '90%',
              zIndex: 10001
            }}>
              {photos.map((photo, index) => (
                <motion.img
                  key={index}
                  src={photo}
                  alt={`Thumbnail ${index + 1}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(index);
                  }}
                  whileHover={{ scale: 1.1 }}
                  style={{
                    width: '50px',
                    height: '50px',
                    objectFit: 'cover',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: index === currentIndex ? '2px solid white' : '2px solid transparent',
                    opacity: index === currentIndex ? 1 : 0.6,
                    transition: 'all 0.2s'
                  }}
                />
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
});

ImageLightbox.displayName = 'ImageLightbox';

// Star Rating Component
const StarRating = memo(({ rating, size = 16 }) => {
  const stars = useMemo(() => [1, 2, 3, 4, 5], []);
  
  return (
    <div style={{ display: 'flex', gap: '2px' }}>
      {stars.map((star) => (
        <svg
          key={star}
          style={{
            height: `${size}px`,
            width: `${size}px`,
            color: star <= rating ? '#fbbf24' : '#e5e7eb',
            fill: 'currentColor'
          }}
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
});

StarRating.displayName = 'StarRating';

// Rating Bar Component
const RatingBar = memo(({ stars, count, percentage, index }) => {
  const { t } = useTranslation();
  return (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  }}>
    <span style={{
      fontSize: '14px',
      color: '#6b7280',
      width: '48px',
      textAlign: 'left'
    }}>
      {stars} {t('venue.starLabel', { defaultValue: 'Star' })}
    </span>
    <div style={{
      flex: 1,
      backgroundColor: '#f3f4f6',
      borderRadius: '4px',
      height: '8px',
      position: 'relative'
    }}>
      <motion.div 
        initial={{ width: 0 }}
        animate={{ width: `${percentage}%` }}
        transition={{ duration: 0.8, delay: 0.3 + (index * 0.1) }}
        style={{
          backgroundColor: '#111827',
          height: '8px',
          borderRadius: '4px'
        }}
      />
    </div>
    <span style={{
      fontSize: '14px',
      color: '#6b7280',
      width: '32px',
      textAlign: 'right'
    }}>
      {count}
    </span>
  </div>
  );
});

RatingBar.displayName = 'RatingBar';

// Overall Rating Section
const OverallRatingSection = memo(({ overallRating, onWriteReview }) => {
  const { t } = useTranslation();
  const roundedRating = useMemo(() => Math.round(overallRating.average), [overallRating.average]);
  const formattedTotal = useMemo(() => overallRating.total.toLocaleString(), [overallRating.total]);

  return (
    <motion.div
      initial={{ y: 30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.4 }}
      style={{
        backgroundColor: 'white',
        borderRadius: '20px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        padding: '24px',
        marginBottom: '20px'
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: '500',
          color: '#6b7280',
          marginBottom: '16px'
        }}>
          {t('venue.averageRating')}
        </h3>
        
        <div style={{
          fontSize: '36px',
          fontWeight: '700',
          color: '#111827',
          marginBottom: '8px'
        }}>
          {overallRating.average.toFixed(1)}/5
        </div>
        
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '8px'
        }}>
          <StarRating rating={roundedRating} size={24} />
        </div>
        
        <p style={{
          fontSize: '14px',
          color: '#6b7280',
          marginBottom: '24px'
        }}>
          {t('venue.reviewsCount', { count: formattedTotal })}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', textAlign: 'left' }}>
          {overallRating.breakdown.map((item, index) => (
            <RatingBar 
              key={item.stars} 
              stars={item.stars}
              count={item.count}
              percentage={item.percentage}
              index={index}
            />
          ))}
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onWriteReview}
          style={{
            marginTop: '24px',
            width: '100%',
            padding: '14px',
            background: 'linear-gradient(135deg, #ea580c, #f59e0b)',
            color: 'white',
            borderRadius: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            border: 'none',
            boxShadow: '0 4px 12px rgba(234, 88, 12, 0.3)',
            transition: 'all 0.2s'
          }}
        >
          <PencilIcon style={{ height: '18px', width: '18px' }} />
          {t('venue.writeYourReview')}
        </motion.button>
      </div>
    </motion.div>
  );
});

OverallRatingSection.displayName = 'OverallRatingSection';

// Review Photo Component
const ReviewPhoto = memo(({ photo, index, totalPhotos, onClick }) => {
  const [loaded, setLoaded] = useState(false);

  if (index >= 4 && totalPhotos > 4) {
    return (
      <div 
        onClick={() => onClick(4)}
        style={{
          width: '60px',
          height: '60px',
          borderRadius: '8px',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '12px',
          fontWeight: '600',
          cursor: 'pointer'
        }}
      >
        +{totalPhotos - 4}
      </div>
    );
  }

  if (index >= 4) return null;

  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      onClick={() => onClick(index)}
      style={{
        width: '60px',
        height: '60px',
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        backgroundColor: '#f3f4f6',
        position: 'relative'
      }}
    >
      <img
        src={photo}
        alt={`Review photo ${index + 1}`}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease'
        }}
      />
    </motion.div>
  );
});

ReviewPhoto.displayName = 'ReviewPhoto';

// Individual Review Component
const ReviewItem = memo(({ review, index, isLast, onHelpfulClick, currentUserId }) => {
  const { t } = useTranslation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const handleHelpfulClick = useCallback(() => {
    onHelpfulClick(review._id);
  }, [review._id, onHelpfulClick]);

  const handlePhotoClick = useCallback((photoIndex) => {
    setLightboxIndex(photoIndex);
    setLightboxOpen(true);
  }, []);

  const userHelpful = review.helpfulBy?.includes(currentUserId);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08 * index }}
        style={{
          borderBottom: isLast ? 'none' : '1px solid #f3f4f6',
          paddingBottom: isLast ? 0 : '24px'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <img
            src={review.user?.profileImage || `https://ui-avatars.com/api/?name=${review.user?.name || 'User'}&background=random`}
            alt={review.user?.name}
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%'
            }}
          />
          <div style={{ flex: 1 }}>
            <h4 style={{
              fontWeight: '600',
              color: '#111827',
              margin: 0,
              fontSize: '15px'
            }}>
              {review.user?.name || t('venue.anonymousUser')}
            </h4>
            <p style={{
              fontSize: '12px',
              color: '#6b7280',
              margin: 0
            }}>
              {new Date(review.createdAt).toLocaleDateString(dtLocale(), { 
                day: '2-digit', 
                month: 'short', 
                year: 'numeric' 
              })}
            </p>
          </div>
        </div>

        <div style={{ marginBottom: '8px' }}>
          <StarRating rating={review.rating} />
          {review.title && (
            <h5 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#111827',
              margin: '8px 0 4px 0'
            }}>
              {review.title}
            </h5>
          )}
        </div>

        {review.verified && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginBottom: '12px'
          }}>
            <CheckIcon style={{ width: '14px', color: '#ea580c' }} />
            <span style={{
              color: '#ea580c',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {t('venue.verifiedPurchase')}
            </span>
          </div>
        )}

        {review.comment && (
          <p style={{
            color: '#374151',
            marginBottom: '16px',
            lineHeight: '1.6',
            fontSize: '14px'
          }}>
            {review.comment}
          </p>
        )}

        {review.tags && review.tags.length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px',
            marginBottom: '16px'
          }}>
            {review.tags.map((tag, tagIndex) => (
              <span
                key={tagIndex}
                style={{
                  padding: '4px 10px',
                  background: '#dbeafe',
                  color: '#1e40af',
                  borderRadius: 12,
                  fontSize: 11,
                  fontWeight: 600
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {review.photos && review.photos.length > 0 && (
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '16px',
            flexWrap: 'wrap'
          }}>
            {review.photos.map((photo, photoIndex) => (
              <ReviewPhoto
                key={photoIndex}
                photo={photo}
                index={photoIndex}
                totalPhotos={review.photos.length}
                onClick={handlePhotoClick}
              />
            ))}
          </div>
        )}

        <div>
          <div style={{
            fontSize: '14px',
            color: '#6b7280',
            marginBottom: '8px'
          }}>
            {t('venue.wasThisHelpful')}
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleHelpfulClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: userHelpful ? '#ea580c' : '#6b7280',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: userHelpful ? '#fef3c7' : 'transparent',
              transition: 'all 0.2s'
            }}
          >
            {userHelpful ? (
              <ThumbUpSolid style={{ height: '16px', width: '16px' }} />
            ) : (
              <HandThumbUpIcon style={{ height: '16px', width: '16px' }} />
            )}
            <span style={{ fontWeight: '500' }}>
              {userHelpful ? t('venue.helpful') : t('venue.thank')} ({review.helpful || 0})
            </span>
          </motion.button>
        </div>
      </motion.div>

      <ImageLightbox
        photos={review.photos || []}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
});

ReviewItem.displayName = 'ReviewItem';

// Pagination Controls
const PaginationControls = memo(({ currentPage, totalPages, onPageChange }) => {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      marginTop: '32px',
      paddingTop: '24px',
      borderTop: '1px solid #f3f4f6'
    }}>
      <button 
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={{
          padding: '8px 12px',
          color: currentPage === 1 ? '#d1d5db' : '#6b7280',
          background: 'none',
          border: 'none',
          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
          fontSize: '16px'
        }}
      >
        ‹
      </button>
      
      {[...Array(totalPages)].map((_, index) => (
        <button
          key={index + 1}
          onClick={() => onPageChange(index + 1)}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            fontWeight: '500',
            transition: 'all 0.2s',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
            backgroundColor: currentPage === index + 1 ? '#ea580c' : 'transparent',
            color: currentPage === index + 1 ? 'white' : '#6b7280'
          }}
        >
          {index + 1}
        </button>
      ))}
      
      <button 
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        style={{
          padding: '8px 12px',
          color: currentPage === totalPages ? '#d1d5db' : '#6b7280',
          background: 'none',
          border: 'none',
          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
          fontSize: '16px'
        }}
      >
        ›
      </button>
    </div>
  );
});

PaginationControls.displayName = 'PaginationControls';

// Normalize Neon review rows (snake_case) to the shape ReviewItem expects
function normalizeNeonReview(r) {
  return {
    _id: String(r.id),
    rating: r.rating,
    title: r.title || null,
    comment: r.comment || null,
    tags: r.tags || [],
    photos: r.photos || [],
    verified: r.verified || false,
    helpful: r.helpful_count || 0,
    helpfulBy: [],
    createdAt: r.created_at,
    user: null, // no user join in venue API response
  };
}

// Compute overall rating stats from a reviews array
function computeOverallRating(reviews) {
  if (!reviews.length) return { average: 0, total: 0, breakdown: [] };
  const total = reviews.length;
  const average = reviews.reduce((s, r) => s + (r.rating || 0), 0) / total;
  const counts = [5, 4, 3, 2, 1].map(star => ({
    stars: star,
    count: reviews.filter(r => r.rating === star).length,
    percentage: Math.round((reviews.filter(r => r.rating === star).length / total) * 100),
  }));
  return { average, total, breakdown: counts };
}

// ✅ FIXED: Main ReviewsSection Component
// neonReviews: optional array of already-fetched Neon review rows — skips API fetch when provided
const ReviewsSection = ({ venue, neonReviews }) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [showWriteModal, setShowWriteModal] = useState(false);
  const [reviews, setReviews] = useState(() =>
    neonReviews && neonReviews.length > 0 ? neonReviews.map(normalizeNeonReview) : []
  );
  const [loading, setLoading] = useState(!neonReviews);
  const [overallRating, setOverallRating] = useState(() =>
    neonReviews && neonReviews.length > 0
      ? computeOverallRating(neonReviews.map(normalizeNeonReview))
      : { average: 0, total: 0, breakdown: [] }
  );
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalReviews: 0
  });

  // ✅ Prevent memory leaks
  const isMountedRef = useRef(true);
  const fetchAbortControllerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cancel any pending requests
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
      }
    };
  }, []);

  const venueId = useMemo(() => venue?.mongo_id || venue?._id, [venue]);
  const region = useMemo(() => venue?.region || 'th', [venue]);
  const token = localStorage.getItem('venue_app_token');
  const currentUserId = localStorage.getItem('userId');

  const fetchReviews = useCallback(async (page = 1) => {
    if (neonReviews) return; // Already initialized from neonReviews prop
    if (!venueId) return;

    // Cancel previous request
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }

    // Create new abort controller
    fetchAbortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      const response = await fetch(
        `/api/reviews/venue/${venueId}?page=${page}&limit=5`,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-region': region,
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          signal: fetchAbortControllerRef.current.signal
        }
      );

      if (!response.ok) throw new Error('Failed to fetch reviews');

      const data = await response.json();
      
      // ✅ Only update state if component is still mounted
      if (isMountedRef.current) {
        setReviews(data.reviews || []);
        setPagination(data.pagination || {});
        setOverallRating(data.stats || { average: 0, total: 0, breakdown: [] });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted');
        return;
      }
      console.error('Error fetching reviews:', error);
      if (isMountedRef.current) {
        setReviews([]);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [venueId, region, token]);

  useEffect(() => {
    fetchReviews(currentPage);
  }, [currentPage, fetchReviews]);

  // ✅ FIXED: Better review submission handler
  const handleSubmitReview = useCallback(async (newReview) => {
    console.log('✅ New review submitted:', newReview);
    
    // ✅ Refresh reviews list
    setCurrentPage(1);
    await fetchReviews(1);
    
    // ✅ Close modal
    setShowWriteModal(false);
  }, [fetchReviews]);

  const handleHelpfulClick = useCallback(async (reviewId) => {
    if (!token) {
      alert(t('venue.loginToVote'));
      return;
    }

    try {
      const response = await fetch(
        `/api/reviews/${reviewId}/helpful`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-region': region
          }
        }
      );

      if (!response.ok) throw new Error('Failed to update helpful status');

      const data = await response.json();
      
      if (isMountedRef.current) {
        setReviews(prevReviews =>
          prevReviews.map(review =>
            review._id === reviewId
              ? { 
                  ...review, 
                  helpful: data.helpful, 
                  helpfulBy: data.userHelpful 
                    ? [...(review.helpfulBy || []), currentUserId] 
                    : (review.helpfulBy || []).filter(id => id !== currentUserId)
                }
              : review
          )
        );
      }
    } catch (error) {
      console.error('Error toggling helpful:', error);
    }
  }, [token, region, currentUserId, t]);

  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pagination.totalPages]);

  if (!venueId) return null;

  return (
    <>
      <OverallRatingSection 
        overallRating={overallRating}
        onWriteReview={() => setShowWriteModal(true)}
      />

      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          padding: '24px'
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            {t('venue.loadingReviews')}
          </div>
        ) : reviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            {t('venue.noReviewsYet')}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {reviews.map((review, index) => (
                <ReviewItem
                  key={review._id}
                  review={review}
                  index={index}
                  isLast={index === reviews.length - 1}
                  onHelpfulClick={handleHelpfulClick}
                  currentUserId={currentUserId}
                />
              ))}
            </div>

            {pagination.totalPages > 1 && (
              <PaginationControls
                currentPage={currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </motion.div>

      <WriteReviewModal
        isOpen={showWriteModal}
        onClose={() => setShowWriteModal(false)}
        onSubmit={handleSubmitReview}
        venue={venue}
      />
    </>
  );
};

export default memo(ReviewsSection);